import { IAccountConciliation } from "@/@types/deposit-conference";
import { Paginated } from "src/@types/paginated";
import {
  useDeleteFileMutation,
  useGetFilesQuery,
  useSignedUrlMutation,
} from "@/services/file";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { toast } from "components/ui/use-toast";
import { Download, History, Info, MoreHorizontal } from "lucide-react";
import React, { useState } from "react";

import DownloadNsuEvidence, { FileData } from "../download-nsu-evidence";
import {
  FILE_MESSAGES,
  MORE_INFO_LABELS,
  ROW_ACTIONS_LABELS,
} from "../utils/messages";
import { NsuHistoryContent } from "./nsu-history-content";
import { NsuMoreInfoContent } from "./nsu-more-info-content";

interface NsuRowActionsProps {
  nsu: IAccountConciliation;
}

/**
 * Consolida as ações da linha num menu só. Antes o Download e o Histórico eram duas
 * colunas separadas, cada uma com seu ícone, o que ajudava a esticar a tabela.
 * Os hooks de arquivo vivem aqui porque antes estavam dentro do cell da coluna.
 */
export const NsuRowActions: React.FC<NsuRowActionsProps> = ({ nsu }) => {
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);
  const { data: filesResponse, isLoading: isLoadingFiles } = useGetFilesQuery({
    entity_name: "NSU",
    id: nsu.id.toString(),
  }) as { data?: Paginated<FileData>; isLoading: boolean };
  const [signedUrl, { isLoading: isDownloading }] = useSignedUrlMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();

  const files = filesResponse?.data ?? [];
  const hasFiles = !isLoadingFiles && files.length > 0;

  const handleDownload = async (fileId: string) => {
    try {
      const response = await signedUrl({ id: fileId }).unwrap();
      window.open(response.url);
    } catch (error) {
      toast(FILE_MESSAGES.DOWNLOAD_ERROR);
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await deleteFile({ id: fileId }).unwrap();
      toast(FILE_MESSAGES.DELETE_SUCCESS);
    } catch (error) {
      toast(FILE_MESSAGES.DELETE_ERROR);
    }
  };

  return (
    <div className="flex items-center justify-center w-full">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost_none"
            className="relative flex h-8 w-8 p-0 cursor-pointer rounded-md transition-colors hover:bg-[#e4e4e7] data-[state=open]:bg-[#e4e4e7] border-none bg-transparent"
          >
            <MoreHorizontal className="h-4 w-4" />
            {hasFiles && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-green-500" />
            )}
            <span className="sr-only">{ROW_ACTIONS_LABELS.MENU_ARIA}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[190px]">
          <DropdownMenuItem
            onClick={() => setIsDownloadOpen(true)}
            disabled={!nsu.id}
            className="cursor-pointer focus:bg-[#efefef] hover:bg-[#efefef] data-[highlighted]:bg-[#efefef]"
          >
            <Download className="mr-2 h-4 w-4" />
            {ROW_ACTIONS_LABELS.DOWNLOAD}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setIsHistoryOpen(true)}
            className="cursor-pointer focus:bg-[#efefef] hover:bg-[#efefef] data-[highlighted]:bg-[#efefef]"
          >
            <History className="mr-2 h-4 w-4" />
            {ROW_ACTIONS_LABELS.HISTORY}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setIsMoreInfoOpen(true)}
            className="cursor-pointer focus:bg-[#efefef] hover:bg-[#efefef] data-[highlighted]:bg-[#efefef]"
          >
            <Info className="mr-2 h-4 w-4" />
            {MORE_INFO_LABELS.TITLE}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog local em vez de handleModal: o handleModal substitui o modal aberto e
          derrubaria a tela do Registro NSU, que também é um modal dele. Aqui o "voltar"
          do Download é só fechar, e a tela de baixo continua intacta. */}
      <Dialog open={isDownloadOpen} onOpenChange={setIsDownloadOpen}>
        <DialogContent className="w-auto max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{ROW_ACTIONS_LABELS.DOWNLOAD_DIALOG_TITLE}</DialogTitle>
          </DialogHeader>
          <DownloadNsuEvidence
            files={files}
            type="NSU"
            shouldBackTo={() => setIsDownloadOpen(false)}
            isClosed={false}
            onDownload={handleDownload}
            onDelete={handleDelete}
            isDownloading={isDownloading}
            isDeleting={isDeleting}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="w-auto max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{ROW_ACTIONS_LABELS.HISTORY_DIALOG_TITLE}</DialogTitle>
          </DialogHeader>
          {isHistoryOpen && <NsuHistoryContent nsuId={nsu.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={isMoreInfoOpen} onOpenChange={setIsMoreInfoOpen}>
        <DialogContent className="w-auto max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{MORE_INFO_LABELS.TITLE}</DialogTitle>
          </DialogHeader>
          {isMoreInfoOpen && <NsuMoreInfoContent nsu={nsu} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
